import { yupResolver } from '@hookform/resolvers/yup';
import { type FocusEvent, memo, useCallback, useEffect, useId, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { type LeadFormUILocation, trackGTMEvent, useTrackClickedLeadFormSubmit } from '@air/analytics';
import { Button } from '@air/primitive-button';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { DemoFormCompanySizeField } from '~/components/Modals/DemoFormCompanySizeField';
import { DemoFormTextField } from '~/components/Modals/DemoFormTextField';
import { DemoSchedulerModal } from '~/components/Modals/DemoSchedulerModal';
import { type DemoFormInput, DemoFormValidationSchema } from '~/components/Modals/demoFormValidation';
import {
  DEFAULT_PIXEL_IN_APP_DEMO_FORM_HTML_ID,
  DEFAULT_PIXEL_IN_APP_DEMO_FORM_ID,
  DEMO_FORM_SUBMIT_ERROR_MESSAGE,
} from '~/constants/defaultPixel';
import { Routes } from '~/constants/routes';
import { useSubmitDemoLeadFormMutation } from '~/hooks/queries/mutations/default-pixel/useSubmitDemoLeadFormMutation';
import { useDefaultPixelSdkReady } from '~/hooks/useDefaultPixelSdkReady';
import { useAccountContext } from '~/providers/AccountProvider';

export type DemoFormProps = {
  onClose: () => void;
  uiLocation: LeadFormUILocation | undefined;
};

export const DemoForm = memo(({ onClose, uiLocation }: DemoFormProps) => {
  const formId = useId();
  const { data: account } = useAccountContext();
  const { trackClickedLeadFormSubmit } = useTrackClickedLeadFormSubmit();
  const { showToast } = useToasts();
  const { mutateAsync: submitDemoLeadForm } = useSubmitDemoLeadFormMutation();
  const { isDefaultPixelSdkReady } = useDefaultPixelSdkReady();
  const [showDemoSchedulerModal] = useAirModal(DemoSchedulerModal);

  const defaultValues = useMemo(
    (): DemoFormInput => ({
      firstName: account?.firstName ?? '',
      lastName: account?.lastName ?? '',
      email: account?.email ?? '',
      company_size: '',
      phone: account?.phone ?? '',
      message: '',
    }),
    [account?.email, account?.firstName, account?.lastName, account?.phone],
  );

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    register,
    reset,
  } = useForm<DemoFormInput>({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(DemoFormValidationSchema),
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const recordEmailInteraction = useCallback((email: string) => {
    if (!DEFAULT_PIXEL_IN_APP_DEMO_FORM_HTML_ID) {
      return;
    }

    window.DefaultPixelSDK?.recordFormInteraction({
      formId: DEFAULT_PIXEL_IN_APP_DEMO_FORM_HTML_ID,
      fieldName: 'email',
      fieldValue: email,
      fieldType: 'email',
    });
  }, []);

  const handleEmailBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      recordEmailInteraction(event.currentTarget.value);
    },
    [recordEmailInteraction],
  );

  const onSubmit = useCallback(
    async (values: DemoFormInput) => {
      trackClickedLeadFormSubmit({ email: values.email, ui_location: uiLocation });

      trackGTMEvent({
        event: `Lead Form Submit`,
        eventProps: {
          label: 'lead form submit',
          category: 'enterprise',
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          company_size: values.company_size,
          phone: values.phone,
          message: values.message,
        },
      });

      try {
        const result = await submitDemoLeadForm(values);
        const schedulerUrl = result.scheduler?.url;

        if (!schedulerUrl) {
          showToast('Your request was sent. The booking calendar is not available for this form yet.');
          onClose();
          return;
        }

        showDemoSchedulerModal({ schedulerUrl });
        onClose();
      } catch {
        showToast(DEMO_FORM_SUBMIT_ERROR_MESSAGE, { color: 'red' });
      }
    },
    [onClose, showDemoSchedulerModal, showToast, submitDemoLeadForm, trackClickedLeadFormSubmit, uiLocation],
  );

  const handleFormSubmit = useMemo(() => handleSubmit(onSubmit), [handleSubmit, onSubmit]);

  return (
    <>
      <form
        className="flex flex-col gap-6"
        data-default-form-id={DEFAULT_PIXEL_IN_APP_DEMO_FORM_ID}
        data-default-ignore
        data-pixel-form-id={DEFAULT_PIXEL_IN_APP_DEMO_FORM_ID}
        id={DEFAULT_PIXEL_IN_APP_DEMO_FORM_HTML_ID}
        onSubmit={handleFormSubmit}
      >
        <div className="flex flex-col gap-4 md:flex-row">
          <DemoFormTextField
            autoComplete="given-name"
            errorMessage={errors.firstName?.message}
            fieldId={`${formId}-firstName`}
            label="First name"
            placeholder="First name*"
            registerProps={register('firstName')}
            required
            type="text"
          />
          <DemoFormTextField
            autoComplete="family-name"
            errorMessage={errors.lastName?.message}
            fieldId={`${formId}-lastName`}
            label="Last name"
            placeholder="Last name*"
            registerProps={register('lastName')}
            required
            type="text"
          />
        </div>

        <DemoFormTextField
          autoComplete="email"
          errorMessage={errors.email?.message}
          fieldId={`${formId}-email`}
          label="Email address"
          placeholder="Email address*"
          registerProps={register('email', { onBlur: handleEmailBlur })}
          required
          type="email"
        />

        <DemoFormCompanySizeField control={control} formId={formId} />

        <DemoFormTextField
          autoComplete="tel"
          errorMessage={errors.phone?.message}
          fieldId={`${formId}-phone`}
          label="Phone number"
          placeholder="Phone number*"
          registerProps={register('phone')}
          type="tel"
        />

        <DemoFormTextField
          errorMessage={errors.message?.message}
          fieldId={`${formId}-message`}
          label="Message"
          placeholder="Message"
          registerProps={register('message')}
          type="text"
        />

        <Button
          appearance="filled"
          color="blue"
          disabled={isSubmitting || !isValid || !isDefaultPixelSdkReady}
          isLoading={isSubmitting}
          size="large"
          type="submit"
        >
          Submit
        </Button>
      </form>
      <p className="text-center ads:text-secondary text-14 text-gray-11">
        By submitting the form, you agree to our <a href={Routes.marketing.terms}>Terms of Service</a> and acknowledge
        our <a href={Routes.marketing.privacy}>Privacy Policy</a>.
      </p>
    </>
  );
});

DemoForm.displayName = 'DemoForm';
